import CloseIcon from '@mui/icons-material/Close';
import {
  Avatar,
  Box,
  IconButton,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import ProfileIcon from '../../../../assets/profile.jpg';
import api from '../../../../utils/api';
import Button from '../../../common/Button';
import Dialog from '../../../common/Dialog';
import Loading from '../../../common/Loading';
import StatusChip from '../../../common/StatusChip';

interface ProfileDetailsPopupProps {
  open: boolean;
  onClose: () => void;
  profileId: number;
}

interface ProfileData {
  name: string;
  designation: string;
  about: string;
  skills: {
    name: string;
  }[];
  email: string;
  phone: string;
  education: {
    degree: string;
    institute: string;
    from_year: string;
    to_year: string;
  }[];
  work_experience: {
    name: string;
    duration: string;
    skills: string[];
  }[];
  vacancy: string;
  linkedin_profile_url: string;
  linkedin_profile_last_synced_on: string;
}

const ProfileDetailsPopup: React.FC<ProfileDetailsPopupProps> = ({
  open,
  onClose,
  profileId,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);

  useEffect(() => {
    const fetchProfileDetails = async () => {
      if (!open || !profileId) return;

      try {
        setIsLoading(true);
        const response = await api.get<{ data: ProfileData }>(`/rec/profile/search/${profileId}`, {
          headers: {
            'x-api-id': 3,
            'x-page-id': 6,
            'x-token': 'PSBrwdyDEe4tP8E1X89lEjeD3e7IpebrFtypPjwFD9S7baxiRhIxP5dyLJuqrxV0'
          }
        });
        setProfileData(response.data.data);
      } catch (error) {
        console.error('Error fetching profile details:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileDetails();
  }, [open, profileId]);

  if (!profileData) {
    return (
      <Dialog open={open} onClose={onClose}>
        <Box sx={{ position: 'relative', p: 3 }}>
          {isLoading ? (
            <Loading message="Loading profile details..." />
          ) : (
            <Typography>No profile data available</Typography>
          )}
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <Box sx={{ position: 'relative', p: 3 }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: '#6c5ce7',
                fontSize: '24px',
              }}
              src={ProfileIcon}
            >
              {!ProfileIcon && profileData.name.charAt(0)}
            </Avatar>
            <Box sx={{ ml: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '24px' }}>
                {profileData.name}
              </Typography>
              <Typography variant="body2" color="#00000066" sx={{ fontSize: '16px', fontWeight: 500 }}>
                {profileData.designation}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 1, lineHeight: "100%", fontSize: '19px', fontWeight: 600 }}>About</Typography>
            <Typography variant="body2" sx={{ lineHeight: "150%", fontSize: '14px', fontWeight: 500 }}>
              {profileData.about}
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color='#00000066' sx={{ fontSize: '12px', fontWeight: 500 }}>Skills and Technologies</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {profileData.skills.map((skill) => (
                <StatusChip key={skill.name} label={skill.name} className='view-tag' />
              ))}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', mb: 4, gap: 8 }}>
            <Box>
              <Typography variant="body2" color='#00000066' sx={{ fontSize: '12px', fontWeight: 500 }}>Email</Typography>
              <Typography variant="body2" color='#000000' sx={{ fontSize: '14px', fontWeight: 500 }}>{profileData.email}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color='#00000066' sx={{ fontSize: '12px', fontWeight: 500 }}>Phone</Typography>
              <Typography variant="body2" color='#000000' sx={{ fontSize: '14px', fontWeight: 500 }}>{profileData.phone}</Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 1, lineHeight: "100%", fontSize: '19px', fontWeight: 600 }}>Education</Typography>
            {profileData.education.map((edu, index) => (
              <Box
                key={index}
                sx={{
                  backgroundColor: '#F1F3FF',
                  p: 2,
                  borderRadius: 1,
                  mb: 1,
                }}
              >
                <Typography variant="body1" color='#000000' sx={{ fontSize: '14px', fontWeight: 500 }}>{edu.degree}</Typography>
                <Typography variant="body2" color='#00000066' sx={{ fontSize: '12px', fontWeight: 500 }}>
                  {edu.from_year} - {edu.to_year}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 1, lineHeight: "100%", fontSize: '19px', fontWeight: 600 }}>Work Experience</Typography>
            {profileData.work_experience.map((exp, index) => (
              <Box
                key={index}
                sx={{
                  backgroundColor: '#F1F3FF',
                  p: 2,
                  borderRadius: 1,
                  mb: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <Box>
                    <Typography variant="body1" color='#000000' sx={{ fontSize: '14px', fontWeight: 500 }}>{"title"} - {exp.name}</Typography>
                    <Typography variant="body2" color='#00000066' sx={{ fontSize: '12px', fontWeight: 500 }}>
                      {exp.duration}
                    </Typography>
                  </Box>
                  {/* <Box>
                    <Typography variant="body2" color='#00000066' sx={{ fontSize: '12px', fontWeight: 500 }}>Skills/Technologies</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {exp.skills.map((skill) => (
                        <StyledChip key={skill} label={skill} />
                      ))}
                    </Box>
                  </Box> */}
                </Box>
              </Box>
            ))}
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ lineHeight: "100%", fontSize: '19px', fontWeight: 600 }}>Vacancy</Typography>
            <Typography variant="body1" color='#000000' sx={{ fontSize: '14px', fontWeight: 500 }}>
              {profileData.vacancy}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 8 }}>
            <Box>
              <Typography variant="body2" color='#00000066' sx={{ fontSize: '12px', fontWeight: 500 }}>LinkedIn Profile URL</Typography>
              <Typography
                variant="body2"
                component="a"
                href={profileData.linkedin_profile_url}
                target="_blank"
                sx={{ color: 'black' }}
              >
                {profileData.linkedin_profile_url}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color='#00000066' sx={{ fontSize: '12px', fontWeight: 500 }}>
                LinkedIn Profile Last Synced On
              </Typography>
              <Typography variant="body2">{profileData.linkedin_profile_last_synced_on}</Typography>
            </Box>
          </Box>

        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button onClick={onClose} variant="outlined" width="120px" style={{ height: '25px' }}>
            Close
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ProfileDetailsPopup; 